// 取り込みファイルの生データ → 一括登録画面の編集行 (TBulkImportRow)。
// visa 同期 (prepareBulkImportApi) が BE で行っているマスタ突合を、ファイル取り込みでは FE のマスタ一覧で行う。
// 突合できた ID を持たせて BulkImportLearnerPage へ渡すため、以降の編集・保存フローは visa 同期と完全に共通になる。

import { TOption } from "@/types/common";
import { dayjs } from "@/utils/dayjs";
import { GENDER_LABEL, LEARNER_PLAN_STATUS, LEARNER_PLAN_STATUS_LABEL, TLearnerPlanStatus } from "@/types/enum";
import { getNextOrCurrentMonday } from "@/features/learner/utils/calculates";
import { STATUS_OPTIONS, TBulkImportRow } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";
import { findOptionByLabel, normalizeMasterLabel } from "@/features/learner/utils/master-matching";
import { TLearnerRowExcel } from "@/features/learner/components/learner-file-import/learner-import-columns";

// 突合に使うマスタ一覧。取り込みモーダル側で hook から集めて渡す。
export type TLearnerImportMasters = {
  // 企業アカウントは所属機関を持たない (列も出さない) ため、所属機関の突合をスキップする。
  isCompanyUser: boolean;
  // RSO 内の所属機関一覧 (value = rsoCompanyId)
  companyOptions: TOption[];
  // 国籍マスタ (value = nationalityId)
  nationalityOptions: TOption[];
  // 受験分野マスタ (value = industryId)
  industryOptions: TOption[];
  // 日本語能力 enum (value = n1..n5 のコード値, label = N1..N5)
  japaneseLevelOptions: TOption[];
  // 性別 enum (value = 1..3)。テンプレートのドロップダウンと同じマスタで突合する。
  genderOptions: TOption[];
  // 受験ステータス enum (value = preparing 等のコード値)
  statusOptions: TOption[];
};

export type TBuildImportRowsResult = {
  rows: TBulkImportRow[];
  // 取り込みを中止すべきエラー (必須項目の未入力・マスタ不一致など)。1件でもあれば取り込まない。
  errors: string[];
};

// 突合ルール (NFKC + 空白除去 + 大文字寄せ) は一括登録画面と共通にする。
// 画面側でも同じ規則で名称→ID を解決するため、規則がずれると「モーダルでは突合、画面では未突合」が起きる。
const normalize = normalizeMasterLabel;

// 日付セル: テンプレートの YYYY/MM/DD 以外にも YYYY-MM-DD / YYYY/M/D を許容する。
const DATE_INPUT_FORMATS = ["YYYY/MM/DD", "YYYY/M/D", "YYYY-MM-DD", "YYYY-M-D"];

// 日付文字列 → YYYY/MM/DD。空文字は "" を返す。解釈できない値は null (=不正) を返す。
const parseDate = (value: string): string | null => {
  if (!value) return "";
  const parsed = dayjs(value, DATE_INPUT_FORMATS, true);
  return parsed.isValid() ? parsed.format("YYYY/MM/DD") : null;
};

// ステータス(日本語ラベル) → planStatus のコード値。
const STATUS_BY_LABEL = Object.entries(LEARNER_PLAN_STATUS_LABEL).reduce<Record<string, TLearnerPlanStatus>>((acc, [value, label]) => {
  acc[normalize(label)] = value as TLearnerPlanStatus;
  return acc;
}, {});

// 性別(日本語ラベル) → GENDER の数値。
const GENDER_BY_LABEL = Object.entries(GENDER_LABEL).reduce<Record<string, number>>((acc, [value, label]) => {
  acc[normalize(label)] = Number(value);
  return acc;
}, {});

// 選択肢の value → 数値 (性別)。数値化できない値は null。
const toNumberOrNull = (value: string | number | undefined): number | null => {
  if (value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

// 0以上の整数か (ブランク期間の検証)。
const isNonNegativeInteger = (value: string): boolean => /^\d+$/.test(value);

export const buildImportRowsFromExcel = (excelRows: TLearnerRowExcel[], masters: TLearnerImportMasters): TBuildImportRowsResult => {
  const { isCompanyUser, companyOptions, nationalityOptions, industryOptions, japaneseLevelOptions, genderOptions, statusOptions } = masters;
  const errors: string[] = [];
  const curriculumStartDate = getNextOrCurrentMonday().format("YYYY/MM/DD");

  const rows: TBulkImportRow[] = [];

  excelRows.forEach((excel, index) => {
    // ファイルの物理行番号 (1行目=ヘッダ) でエラーを示し、ユーザがファイル上で修正箇所を特定できるようにする。
    const rowNumber = index + 2;
    const pushError = (message: string) => errors.push(`${rowNumber}行目：${message}`);

    // ── 必須項目の不備はここで検出し、取り込み自体を中止する (画面で補完させない) ──
    // 氏名が無い行は外国人を特定できないため、行として成立しない。全セル空の行は reader 側で除外済み。
    const fullName = excel.fullName;
    if (!fullName) {
      pushError("必須項目「氏名」が未入力です。");
      return;
    }

    // 所属機関: 企業アカウントは列自体が無いため対象外。RSO/支援ユーザーは必須 (CompanyInRSO への紐付けが要るため)。
    if (!isCompanyUser && !excel.companyName) pushError("必須項目「所属機関」が未入力です。");

    if (!excel.industryName) pushError("必須項目「受験分野」が未入力です。");

    const nationality = findOptionByLabel(nationalityOptions, excel.nationality);
    if (!excel.nationality) pushError("必須項目「国籍」が未入力です。");
    else if (!nationality) pushError(`項目「国籍」（${excel.nationality}）がマスタに存在しません。`);

    // 性別: マスタのラベルで突合し、取得できていない場合は固定ラベル (男性/女性/その他) で救済する。
    const genderValue = toNumberOrNull(findOptionByLabel(genderOptions, excel.gender)?.value) ?? GENDER_BY_LABEL[normalize(excel.gender)] ?? null;
    if (!excel.gender) pushError("必須項目「性別」が未入力です。");
    else if (genderValue == null) pushError(`項目「性別」（${excel.gender}）が不正です。男性、女性、その他のいずれかを入力してください。`);

    const clause1StartDate = parseDate(excel.clause1StartDate);
    if (clause1StartDate === null) pushError(`項目「1号の起算日」（${excel.clause1StartDate}）の日付形式が不正です。YYYY/MM/DD で入力してください。`);

    const lastExamDate = parseDate(excel.lastExamDate);
    if (lastExamDate === null) pushError(`項目「前回試験の受験日」（${excel.lastExamDate}）の日付形式が不正です。YYYY/MM/DD で入力してください。`);

    if (excel.blankPeriodMonths && !isNonNegativeInteger(excel.blankPeriodMonths)) {
      pushError(`項目「ブランク期間（月単位）」（${excel.blankPeriodMonths}）は0以上の整数で入力してください。`);
    }

    // ── 一括登録画面のドロップダウン/入力欄で修正できる項目は、突合できなくても取り込みを止めない ──
    // 受験分野: 未突合は null のまま渡し、画面で必須の赤枠 + 選択で補完させる (visa 同期と同じ挙動)。
    const industry = findOptionByLabel(industryOptions, excel.industryName);
    // 所属機関: 既存に無い名称は「(新規)」として扱い、保存時に BE で CompanyInRSO を作成する (visa 同期と同じ)。
    const company = isCompanyUser ? undefined : findOptionByLabel(companyOptions, excel.companyName);
    const isNewCompany = !isCompanyUser && !company && !!excel.companyName;
    // ステータス: enum マスタのラベル → FE固定ラベルの順に突合。画面のドロップダウンに無い値は「準備中」に寄せる。
    const resolvedStatus = findOptionByLabel(statusOptions, excel.status)?.value ?? STATUS_BY_LABEL[normalize(excel.status)];
    const status = (
      STATUS_OPTIONS.some((opt) => String(opt.value) === String(resolvedStatus)) ? resolvedStatus : LEARNER_PLAN_STATUS.PREPARING
    ) as TLearnerPlanStatus;
    // 日本語能力: ラベル(N4)・コード値(n4)のどちらでも突合する。未突合は空にして画面で選択させる。
    const japaneseLevel =
      findOptionByLabel(japaneseLevelOptions, excel.japaneseLevel)?.value
      ?? japaneseLevelOptions.find((opt) => normalize(String(opt.value)) === normalize(excel.japaneseLevel))?.value;
    // 前回点数: 範囲外(0〜1000の整数以外)は画面のセルで赤枠表示されるため、生値をそのまま渡す。
    const lastScore = excel.lastScore;

    rows.push({
      // ファイル取り込みは visa 従業員IDを持たないため、行番号を UI 上の一意キーにする。
      id: rowNumber,
      visaEmployeeId: null,
      fullName,
      nickName: excel.nickName || null,
      managementNumber: excel.managementNumber || null,
      companyName: isCompanyUser ? "" : excel.companyName,
      isNewCompany,
      rsoCompanyId: company ? Number(company.value) : null,
      nationalityId: nationality ? Number(nationality.value) : null,
      nationality: nationality?.label ?? excel.nationality,
      industryId: industry ? Number(industry.value) : null,
      // 表示名は visa 同期と同じ方針: 突合できたらマスタ名、できなくてもファイルの入力値をそのまま残す
      // (id だけ null にして画面で選び直させる。表示まで消すと何を入力したのか分からなくなる)。
      industryName: industry?.label ?? (excel.industryName || null),
      genderValue,
      // 表示は突合できたマスタ名、できなければファイルの入力値をそのまま出す (visa 同期と同じ方針)。
      gender: genderValue != null ? (GENDER_LABEL[genderValue as keyof typeof GENDER_LABEL] ?? "") : excel.gender,
      status,
      clause1StartDate: clause1StartDate ?? "",
      blankPeriodMonths: excel.blankPeriodMonths ? Number(excel.blankPeriodMonths) : null,
      // ファイル取り込みでは visa の所属機関情報を持たないため null (新規作成は名称のみで行う)。
      company: null,
      lastScore,
      lastExamDate: lastExamDate ?? null,
      procedureManagement: true,
      learningApp: true,
      curriculumStartDate,
      examId: null,
      examScheduleYm: "",
      // 1号残期間はテーブル側で 1号の起算日 + ブランク期間 から自動計算するため保持しない。
      remainingPeriod: "",
      residence: `${excel.province}${excel.municipality}`,
      province: excel.province || null,
      municipality: excel.municipality || null,
      japaneseLevel: japaneseLevel != null ? String(japaneseLevel) : "",
      // 既存登録の判定は visa 従業員ID前提のため、ファイル取り込みでは行わない。
      isAlreadyRegistered: false,
    });
  });

  return { rows, errors };
};
